import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import FilterBar from "../../componentes/filtros/filtros";
import MainCard from "../../componentes/card/MainCard";
import PageAPI from "../../componentes/pagination/Pagination-Page";
import { CustomTable } from "../../componentes/table/CustomTable";
import TiInventarioService, { salvarBlob } from "../../services/resource/tiInventarioService";
import AtivoFormModal from "./AtivoFormModal";
import AtivoDetalheModal from "./AtivoDetalheModal";
import useOpcoesInventario from "./useOpcoesInventario";
import { gerarEtiquetas } from "./etiquetasPdf";
import { FASE_ESTILO, idDoCodigoInventario } from "./tiUtils";

const service = new TiInventarioService();

const FILTROS_INICIAIS = {
    q: "",
    categoria: null,
    filialId: null,
    departamentoId: null,
    statusId: null,
    fase: null,
    tipoDispositivoId: null,
    proprietario: null,
    responsavel: "",
    marcaId: null,
    modeloId: null,
    numeroSerie: "",
    patrimonio: "",
    incluirEncerrados: "true",
};

const STORAGE_COLUNAS = "ti-inventario:colunas";

function lerColunasSalvas() {
    try {
        const salvo = JSON.parse(localStorage.getItem(STORAGE_COLUNAS));
        return Array.isArray(salvo) ? salvo : null;
    } catch {
        return null;
    }
}

export default function InventarioTiPage() {
    document.title = "MyDash | Inventário de TI";
    const navigate = useNavigate();
    const opcoes = useOpcoesInventario(service);
    const { metadados, catalogos, filiais } = opcoes;

    const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
    const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_INICIAIS);
    const [pagina, setPagina] = useState({ page: 0, size: 20, ordenarPor: "nomenclatura", direcao: "asc" });
    const [dados, setDados] = useState({ content: [], number: 0, size: 20, totalElements: 0, totalPages: 0 });
    const [resumo, setResumo] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [colunasVisiveis, setColunasVisiveis] = useState(null);
    const [exportando, setExportando] = useState(false);
    const [novoAtivo, setNovoAtivo] = useState(false);
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const [detalheId, setDetalheId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [selecionados, setSelecionados] = useState([]);
    const [gerandoEtiquetas, setGerandoEtiquetas] = useState(false);
    // recarrega lista e indicadores depois de qualquer alteração feita no modal de detalhes
    const [versao, setVersao] = useState(0);

    // Colunas disponíveis vêm do backend (mesmas chaves usadas na exportação).
    const colunasDisponiveis = useMemo(() => metadados?.colunas || [], [metadados]);
    useEffect(() => {
        if (!metadados || colunasVisiveis) return;
        const validas = new Set(colunasDisponiveis.map(c => c.chave));
        const salvas = (lerColunasSalvas() || []).filter(c => validas.has(c));
        setColunasVisiveis(salvas.length ? salvas : colunasDisponiveis.filter(c => c.padrao).map(c => c.chave));
    }, [metadados, colunasDisponiveis, colunasVisiveis]);

    const alterarColunas = (chaves) => {
        setColunasVisiveis(chaves);
        try { localStorage.setItem(STORAGE_COLUNAS, JSON.stringify(chaves)); } catch { /* ignora */ }
    };

    const parametros = useCallback((f) => {
        const p = { ...f };
        Object.keys(p).forEach(k => { if (p[k] === null || p[k] === "") delete p[k]; });
        return p;
    }, []);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try {
            const res = await service.listarAtivos({ ...parametros(filtrosAplicados), ...pagina });
            setDados(res.data);
        } finally {
            setCarregando(false);
        }
    }, [filtrosAplicados, pagina, parametros]);

    useEffect(() => { buscar(); }, [buscar, versao]);

    // Os indicadores não aplicam o recorte de fase/proprietário escolhido nos próprios cartões; assim todos
    // continuam mostrando a distribuição da filial, departamento, tipo e busca atuais.
    useEffect(() => {
        service.resumo(parametros({ ...filtrosAplicados, fase: null, proprietario: null }))
            .then(res => setResumo(res.data)).catch(() => setResumo(null));
    }, [filtrosAplicados, parametros, versao]);

    // Abre o ativo de uma etiqueta/QR Code (?codigo=TI-000123) e limpa o parâmetro da URL.
    useEffect(() => {
        const codigo = searchParams.get("codigo");
        const id = idDoCodigoInventario(codigo) ?? (Number(searchParams.get("ativo")) || null);
        if (id) {
            setDetalheId(id);
            setSearchParams({}, { replace: true });
        }
        // só na abertura da página com o parâmetro
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtros de um clique (filial, departamento, cartões): aplicam na hora e voltam à primeira página.
    const aplicarImediato = (parcial) => {
        setFiltros(f => ({ ...f, ...parcial }));
        setFiltrosAplicados(f => ({ ...f, ...parcial }));
        setPagina(p => ({ ...p, page: 0 }));
    };

    const alternarFase = (fase) => aplicarImediato({
        fase: filtrosAplicados.fase === fase ? null : fase,
        proprietario: null,
    });

    const alternarAlugados = () => aplicarImediato({
        proprietario: filtrosAplicados.proprietario === "ALUGADO" ? null : "ALUGADO",
        fase: null,
    });

    const quantidadeFase = (fase) => resumo?.porFase?.find(f => f.chave === fase)?.quantidade ?? 0;
    const quantidadeProprietario = (valor) => resumo?.porProprietario?.find(p => p.chave === valor)?.quantidade ?? 0;

    // A seleção vale só para a página exibida; ao trocar de página/filtro ela é descartada.
    useEffect(() => { setSelecionados([]); }, [dados.content]);

    const imprimirEtiquetas = async () => {
        const linhas = selecionados.length
            ? dados.content.filter(a => selecionados.includes(a.id))
            : dados.content;
        if (linhas.length === 0) return;
        setGerandoEtiquetas(true);
        try {
            await gerarEtiquetas(linhas);
        } catch (e) {
            toast.error("Não foi possível gerar as etiquetas.");
        } finally {
            setGerandoEtiquetas(false);
        }
    };

    const aplicarFiltros = () => {
        setPagina(p => ({ ...p, page: 0 }));
        setFiltrosAplicados(filtros);
    };

    const limpar = () => {
        setFiltros(FILTROS_INICIAIS);
        setFiltrosAplicados(FILTROS_INICIAIS);
        setPagina(p => ({ ...p, page: 0 }));
    };

    // Um clique no tipo de dispositivo já recarrega os dados; clicar no tipo ativo volta para "Todos".
    // Só o tipo é aplicado: o que estiver digitado na busca ou no painel continua aguardando "Pesquisar".
    const selecionarTipo = (id) => {
        const novo = id !== null && filtrosAplicados.tipoDispositivoId === id ? null : id;
        setFiltros(f => ({ ...f, tipoDispositivoId: novo }));
        setFiltrosAplicados(f => ({ ...f, tipoDispositivoId: novo }));
        setPagina(p => ({ ...p, page: 0 }));
    };

    const tiposDispositivo = useMemo(
        () => (catalogos.TIPO_DISPOSITIVO || []).filter(t => t.ativo || t.id === filtrosAplicados.tipoDispositivoId),
        [catalogos, filtrosAplicados.tipoDispositivoId]);

    // filtros do painel (exceto busca e tipo) que estão preenchidos no formulário
    const totalFiltrosAvancados = useMemo(() => Object.keys(FILTROS_INICIAIS)
        .filter(k => !["q", "tipoDispositivoId", "filialId", "departamentoId", "fase"].includes(k))
        .filter(k => filtros[k] !== FILTROS_INICIAIS[k] && filtros[k] !== null && filtros[k] !== "").length,
    [filtros]);

    const algumFiltroAplicado = useMemo(
        () => Object.keys(FILTROS_INICIAIS).some(k => filtrosAplicados[k] !== FILTROS_INICIAIS[k])
            || filtros.q !== "" || totalFiltrosAvancados > 0,
        [filtrosAplicados, filtros.q, totalFiltrosAvancados]);

    const exportar = async (formato) => {
        setExportando(true);
        try {
            const res = await service.exportar(parametros(filtrosAplicados), formato, colunasVisiveis);
            salvarBlob(res, `inventario-ti.${formato}`);
        } catch (e) {
            toast.error("Não foi possível exportar os dados.");
        } finally {
            setExportando(false);
        }
    };

    const schema = useMemo(() => [
        // pesquisa livre e tipo de dispositivo ficam fora do painel (campo de busca e chips)
        {
            key: "categoria", label: "Categoria", component: "Dropdown", colSpan: 2,
            options: (metadados?.categorias || []).map(c => ({ label: c.rotulo, value: c.codigo })), showClear: true,
        },
        // filial e departamento ficam fora do painel (filtros rápidos acima dos indicadores)
        {
            key: "statusId", label: "Status", component: "Dropdown", colSpan: 2,
            options: (catalogos.STATUS || []).map(i => ({ label: i.nome, value: i.id })),
        },
        {
            key: "proprietario", label: "Proprietário", component: "Dropdown", colSpan: 2,
            options: (metadados?.proprietarios || []).map(p => ({ label: p.rotulo, value: p.valor })),
        },
        { key: "responsavel", label: "Responsável (nome)", component: "Input", typeText: "text", colSpan: 2 },
        {
            key: "marcaId", label: "Marca", component: "Dropdown", colSpan: 2,
            options: (catalogos.MARCA || []).map(i => ({ label: i.nome, value: i.id })),
        },
        {
            key: "modeloId", label: "Modelo", component: "Dropdown", colSpan: 2,
            options: (catalogos.MODELO || [])
                .filter(m => !filtros.marcaId || m.paiId === filtros.marcaId)
                .map(i => ({ label: i.nome, value: i.id })),
        },
        { key: "numeroSerie", label: "Número de série", component: "Input", typeText: "text", colSpan: 2 },
        { key: "patrimonio", label: "Patrimônio / nomenclatura", component: "Input", typeText: "text", colSpan: 2 },
        {
            key: "incluirEncerrados", label: "Baixados / descartados", component: "Radio", colSpan: 2,
            options: [{ label: "Incluir", value: "true" }, { label: "Ocultar", value: "false" }],
        },
    ], [metadados, catalogos, filtros.marcaId]);

    const colunasTabela = useMemo(() => {
        const render = {
            status: (v, row) => (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FASE_ESTILO[row.fase] || "bg-muted"}`}>{v || "—"}</span>
            ),
            fase: (v, row) => (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FASE_ESTILO[row.fase] || "bg-muted"}`}>{v || "—"}</span>
            ),
        };
        // chave da coluna (backend) -> propriedade do AtivoDTO
        const propriedade = {
            categoria: "categoriaRotulo", tipoDispositivo: "tipoDispositivoNome", status: "statusNome",
            fase: "faseRotulo", proprietario: "proprietarioRotulo", responsavel: "responsavelNome",
            filial: "filialNome", departamento: "departamentoNome", marca: "marcaNome", modelo: "modeloNome",
            operadora: "operadoraNome", estadoConservacao: "estadoConservacaoRotulo", tipoComputador: "tipoComputadorRotulo",
            equipamentoPrincipal: "equipamentoPrincipalDescricao",
        };
        const ordenaveis = ["nomenclatura", "patrimonio", "numeroSerie", "status", "filial", "departamento",
            "tipoDispositivo", "marca", "modelo", "responsavel", "dataAquisicao", "garantiaAte"];
        return (colunasVisiveis || [])
            .map(chave => colunasDisponiveis.find(c => c.chave === chave))
            .filter(Boolean)
            .map(c => ({
                key: propriedade[c.chave] || c.chave,
                label: c.rotulo,
                sortable: false,
                filterable: false,
                render: render[c.chave],
                format: c.chave === "dataAquisicao" || c.chave === "garantiaAte" ? "date"
                    : c.chave === "valorAquisicao" ? "currency" : undefined,
                ordenavel: ordenaveis.includes(c.chave) ? c.chave : null,
            }));
    }, [colunasVisiveis, colunasDisponiveis]);

    const alternarOrdenacao = (chave) => {
        setPagina(p => ({
            ...p,
            page: 0,
            ordenarPor: chave,
            direcao: p.ordenarPor === chave && p.direcao === "asc" ? "desc" : "asc",
        }));
    };

    return (
        <MainCard
            title="Inventário de TI"
            icon="fa fa-laptop"
            header={
                <div className="flex flex-wrap gap-2">
                    <Button label="Cadastros auxiliares" iconLeft="fa fa-sliders-h" variant="white"
                        onClick={() => navigate("/ti/catalogos")} />
                    <Button
                        label={selecionados.length ? `Etiquetas (${selecionados.length})` : "Etiquetas da página"}
                        iconLeft="fa fa-qrcode" variant="white" loading={gerandoEtiquetas}
                        title="Imprime etiquetas com QR Code e código de inventário dos ativos selecionados (ou de toda a página)"
                        onClick={imprimirEtiquetas} />
                    <Button label="Exportar CSV" iconLeft="fa fa-file-csv" variant="white" loading={exportando}
                        onClick={() => exportar("csv")} />
                    <Button label="Exportar Excel" iconLeft="fa fa-file-excel" variant="white" loading={exportando}
                        onClick={() => exportar("xlsx")} />
                    <Button label="Novo ativo" iconLeft="fa fa-plus" variant="indigo" onClick={() => setNovoAtivo(true)} />
                </div>
            }
        >
            <div className="px-6 pt-4 flex flex-wrap items-end gap-3">
                <div className="w-full sm:w-64">
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Filial</label>
                    <Dropdown
                        className="w-full text-sm"
                        value={filtrosAplicados.filialId}
                        options={filiais.map(f => ({ label: f.nome, value: f.id }))}
                        onChange={e => aplicarImediato({ filialId: e.value ?? null })}
                        placeholder="Todas as filiais"
                        showClear
                        filter
                    />
                </div>
                <div className="w-full sm:w-64">
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Departamento</label>
                    <Dropdown
                        className="w-full text-sm"
                        value={filtrosAplicados.departamentoId}
                        options={(catalogos.DEPARTAMENTO || []).map(i => ({ label: i.nome, value: i.id }))}
                        onChange={e => aplicarImediato({ departamentoId: e.value ?? null })}
                        placeholder="Todos os departamentos"
                        showClear
                        filter
                    />
                </div>
                <p className="text-xs text-muted-foreground pb-2">
                    Os indicadores abaixo seguem a filial, o departamento e demais filtros. Clique em um indicador para filtrar a lista.
                </p>
            </div>

            {resumo && (
                <div className="px-6 pt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                    <CartaoIndicador icone="fa fa-boxes" rotulo="Total de ativos" valor={resumo.total}
                        ativo={!filtrosAplicados.fase && !filtrosAplicados.proprietario}
                        onClick={() => aplicarImediato({ fase: null, proprietario: null })} />
                    <CartaoIndicador icone="fa fa-user-check" rotulo="Em uso" valor={quantidadeFase("EM_USO")}
                        ativo={filtrosAplicados.fase === "EM_USO"} onClick={() => alternarFase("EM_USO")} />
                    <CartaoIndicador icone="fa fa-warehouse" rotulo="Em estoque" valor={quantidadeFase("EM_ESTOQUE")}
                        sub={quantidadeFase("DISPONIVEL") > 0 ? `${quantidadeFase("DISPONIVEL")} disponíveis` : null}
                        ativo={filtrosAplicados.fase === "EM_ESTOQUE"} onClick={() => alternarFase("EM_ESTOQUE")} />
                    <CartaoIndicador icone="fa fa-tools" rotulo="Em manutenção" valor={quantidadeFase("EM_MANUTENCAO")}
                        ativo={filtrosAplicados.fase === "EM_MANUTENCAO"} onClick={() => alternarFase("EM_MANUTENCAO")} />
                    <CartaoIndicador icone="fa fa-file-signature" rotulo="Alugados" valor={quantidadeProprietario("ALUGADO")}
                        ativo={filtrosAplicados.proprietario === "ALUGADO"} onClick={alternarAlugados} />
                    <CartaoIndicador icone="fa fa-ban" rotulo="Baixados" valor={quantidadeFase("BAIXADO")}
                        sub={quantidadeFase("DESCARTADO") > 0 ? `${quantidadeFase("DESCARTADO")} descartados` : null}
                        ativo={filtrosAplicados.fase === "BAIXADO"} onClick={() => alternarFase("BAIXADO")} />
                </div>
            )}

            {resumo && (
                <div className="px-6 pt-3 flex flex-wrap items-center gap-2">
                    {resumo.porCategoria.filter(c => c.quantidade > 0).map(c => (
                        <span key={c.chave} className="px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                            {c.rotulo}: {c.quantidade}
                        </span>
                    ))}
                    {resumo.porFase
                        .filter(f => f.quantidade > 0 && !["EM_USO", "EM_ESTOQUE", "EM_MANUTENCAO", "BAIXADO", "DESCARTADO"].includes(f.chave))
                        .map(f => (
                            <span key={f.chave} className={`px-2.5 py-1 rounded-full text-xs font-medium ${FASE_ESTILO[f.chave]}`}>
                                {f.rotulo}: {f.quantidade}
                            </span>
                        ))}
                    {resumo.garantiaVencida > 0 && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Garantia vencida: {resumo.garantiaVencida}
                        </span>
                    )}
                    {resumo.garantiaVencendo30Dias > 0 && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Garantia vence em 30 dias: {resumo.garantiaVencendo30Dias}
                        </span>
                    )}
                </div>
            )}
            <div className="p-6 pt-4 border-b border-border">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[260px]">
                        <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm" />
                        <input
                            className="w-full h-9 pl-9 pr-3 rounded-md border border-border bg-card text-sm text-foreground"
                            placeholder="Pesquisar por nomenclatura, patrimônio, série, MAC, IP, IMEI, usuário, marca, modelo..."
                            value={filtros.q}
                            onChange={e => setFiltros(f => ({ ...f, q: e.target.value }))}
                            onKeyDown={e => { if (e.key === "Enter") aplicarFiltros(); }}
                        />
                    </div>
                    <Button label="Pesquisar" iconLeft="fa fa-search" variant="indigo" onClick={aplicarFiltros} />
                    <Button
                        label={`${mostrarFiltros ? "Ocultar" : "Mostrar"} filtros${totalFiltrosAvancados ? ` (${totalFiltrosAvancados})` : ""}`}
                        iconLeft={mostrarFiltros ? "fa fa-chevron-up" : "fa fa-sliders-h"}
                        variant="white"
                        onClick={() => setMostrarFiltros(v => !v)}
                    />
                    {algumFiltroAplicado && (
                        <Button label="Limpar" iconLeft="fa fa-eraser" variant="gray" onClick={limpar} />
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                    <span className="text-xs text-muted-foreground mr-1">Dispositivo:</span>
                    <ChipTipo ativo={!filtrosAplicados.tipoDispositivoId} onClick={() => selecionarTipo(null)}>Todos</ChipTipo>
                    {tiposDispositivo.map(t => (
                        <ChipTipo key={t.id} ativo={filtrosAplicados.tipoDispositivoId === t.id}
                            onClick={() => selecionarTipo(t.id)}>
                            {t.nome}
                        </ChipTipo>
                    ))}
                </div>

                {mostrarFiltros && (
                    <div className="mt-2">
                        <FilterBar
                            data={{}}
                            filters={filtros}
                            onFiltersChange={setFiltros}
                            schema={schema}
                            columns={8}
                            // o "Limpar" embutido só zera o formulário; o botão da barra acima também reaplica a consulta
                            initialFilters={filtros}
                        />
                        <div className="flex gap-2 mt-4">
                            <Button label="Aplicar filtros" iconLeft="fa fa-check" variant="indigo" onClick={aplicarFiltros} />
                        </div>
                    </div>
                )}
            </div>

            <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
                <label className="text-xs text-muted-foreground">Colunas exibidas</label>
                <MultiSelect
                    value={colunasVisiveis || []}
                    options={colunasDisponiveis.map(c => ({ label: c.rotulo, value: c.chave }))}
                    onChange={e => alterarColunas(e.value)}
                    display="chip"
                    filter
                    maxSelectedLabels={3}
                    placeholder="Selecione as colunas"
                    className="w-full md:w-96 text-xs"
                />
            </div>

            <div className="px-6 pt-3">
                <div className="flex flex-wrap gap-1 mb-2 text-xs text-muted-foreground">
                    Ordenar por:
                    {colunasTabela.filter(c => c.ordenavel).map(c => (
                        <button key={c.ordenavel} onClick={() => alternarOrdenacao(c.ordenavel)}
                            className={`px-2 py-0.5 rounded border ${pagina.ordenarPor === c.ordenavel ? "border-primary text-primary" : "border-border"}`}>
                            {c.label}{pagina.ordenarPor === c.ordenavel ? (pagina.direcao === "asc" ? " ↑" : " ↓") : ""}
                        </button>
                    ))}
                </div>
                <CustomTable
                    data={dados.content}
                    columns={colunasTabela}
                    loading={carregando}
                    className="text-[11px]"
                    selectable
                    rowKey="id"
                    selectedKeys={selecionados}
                    onSelectionChange={setSelecionados}
                    onRowSelect={(row) => setDetalheId(row.id)}
                />
                <PageAPI
                    page={dados.number}
                    size={dados.size}
                    totalElements={dados.totalElements}
                    totalPages={dados.totalPages}
                    loading={carregando}
                    onPageChange={(n) => setPagina(p => ({ ...p, page: n }))}
                    onPageSizeChange={(s) => setPagina(p => ({ ...p, size: s, page: 0 }))}
                    pageSizeOptions={[20, 50, 100, 200]}
                />
            </div>

            <AtivoFormModal
                visible={novoAtivo}
                onHide={() => setNovoAtivo(false)}
                service={service}
                ativo={null}
                opcoes={opcoes}
                onSalvo={(a) => { setVersao(v => v + 1); setDetalheId(a.id); }}
            />

            <AtivoDetalheModal
                visible={detalheId !== null}
                ativoId={detalheId}
                onHide={() => setDetalheId(null)}
                service={service}
                opcoes={opcoes}
                onAlterado={() => setVersao(v => v + 1)}
            />
        </MainCard>
    );
}

function ChipTipo({ ativo, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={ativo}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${ativo
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-foreground border-border hover:bg-muted"}`}
        >
            {children}
        </button>
    );
}

function CartaoIndicador({ icone, rotulo, valor, sub, ativo, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={ativo}
            className={`text-left rounded-xl border p-3 transition-colors ${ativo
                ? "border-primary bg-primary/5 ring-1 ring-primary/40"
                : "border-border bg-card hover:bg-muted/50"}`}
        >
            <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs font-medium">{rotulo}</span>
                <i className={`${icone} text-sm`} />
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">{valor ?? 0}</p>
            <p className="text-[11px] text-muted-foreground min-h-[14px]">{sub || ""}</p>
        </button>
    );
}
